import React, { useEffect, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'sonner';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import SettingsModal from '../../Settings/SettingsModal';
import AccountSelect from '../../Common/AccountSelect';
import { brandSelectTheme } from '../../Common/reactSelectTheme';
import { accountOption, buildAccountPickerOptions } from '../../../../utils/accountPickerOptions';
import { addToQueue, apiError } from './paymentsApi';

const SCHEME_OPTIONS = [
  { value: 'BGNR', label: 'Bankgiro' },
  { value: 'PGNR', label: 'Plusgiro' },
  { value: 'IBAN', label: 'IBAN' },
  { value: 'BBAN', label: 'Bankkonto (clearing + kontonummer)' },
];
const REF_OPTIONS = [
  { value: 'SCOR', label: 'OCR-nummer' },
  { value: 'USTRD', label: 'Meddelande' },
  { value: 'NONE', label: 'Ingen referens' },
];

/** Next weekday from today (the server validates real bank days, holidays included). */
const nextWeekdayIso = () => {
  const d = new Date();
  while ([0, 6].includes(d.getDay())) d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * "Ny manuell betalning" (plan Q8): a typed one-off SUPP payment. Everything
 * is validated server-side (checksums, OCR, bank day, counter account);
 * this form only collects it. The counter account is what the payment is
 * booked against when the bank debit is settled.
 */
const ManualPaymentDialog = ({ show, companyId, onClose, onQueued }) => {
  const [scheme, setScheme] = useState(SCHEME_OPTIONS[0]);
  const [refType, setRefType] = useState(REF_OPTIONS[1]);
  const [form, setForm] = useState({ payee_name: '', identifier: '', clearing_number: '', bic: '', amount: '', requested_date: nextWeekdayIso(), remittance_ref: '' });
  const [account, setAccount] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!show) return;
    setError(null);
    axios.get(getApiUrl(`${companyId}/accounting/accounts?scope=company`), { withCredentials: true })
      .then((r) => setAccounts((r.data.accounts || []).map((a) => accountOption(a, { valueKey: 'account_id' }))))
      .catch(() => setAccounts([]));
  }, [show, companyId]);

  const options = useMemo(() => buildAccountPickerOptions(accounts, { pinned: account ? [account.value] : [] }), [accounts, account]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await addToQueue(companyId, {
        sourceType: 'manual',
        payee_name: form.payee_name,
        scheme: scheme.value,
        identifier: form.identifier,
        clearing_number: scheme.value === 'BBAN' ? form.clearing_number : null,
        bic: scheme.value === 'IBAN' ? form.bic : null,
        amount: form.amount,
        requested_date: form.requested_date,
        remittance_type: refType.value,
        remittance_ref: refType.value === 'NONE' ? null : form.remittance_ref,
        counter_account_id: account ? account.value : null,
      });
      toast.success('Betalningen lades i betalningskön.');
      setForm({ payee_name: '', identifier: '', clearing_number: '', bic: '', amount: '', requested_date: nextWeekdayIso(), remittance_ref: '' });
      setAccount(null);
      onQueued(result);
    } catch (err) {
      const e = apiError(err, 'Kunde inte lägga betalningen i kön.');
      setError(e.details?.errors ? e.details.errors.join(' ') : e.message);
    } finally {
      setBusy(false);
    }
  };

  const footer = (
    <>
      <button type="button" className="ll-btn ll-btn--secondary" onClick={onClose} disabled={busy}>Avbryt</button>
      <button type="button" className="ll-btn ll-btn--primary" onClick={submit} disabled={busy || !form.payee_name || !form.identifier || !form.amount || !account}>{busy ? 'Lägger i kön…' : 'Lägg i betalningskö'}</button>
    </>
  );
  const label = { fontSize: 13, fontWeight: 600 };
  return (
    <SettingsModal show={show} onHide={onClose} title="Ny manuell betalning" footer={footer} size="lg">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
        <Form.Group><Form.Label style={label}>Mottagare</Form.Label><Form.Control value={form.payee_name} onChange={set('payee_name')} placeholder="Namn på mottagaren" /></Form.Group>
        <Form.Group><Form.Label style={label}>Kontotyp</Form.Label><Select options={SCHEME_OPTIONS} value={scheme} onChange={setScheme} theme={brandSelectTheme} isSearchable={false} /></Form.Group>
        {scheme.value === 'BBAN' && <Form.Group><Form.Label style={label}>Clearingnummer</Form.Label><Form.Control value={form.clearing_number} onChange={set('clearing_number')} /></Form.Group>}
        <Form.Group><Form.Label style={label}>{scheme.label}</Form.Label><Form.Control value={form.identifier} onChange={set('identifier')} /></Form.Group>
        {scheme.value === 'IBAN' && <Form.Group><Form.Label style={label}>BIC (valfritt)</Form.Label><Form.Control value={form.bic} onChange={set('bic')} /></Form.Group>}
        <Form.Group><Form.Label style={label}>Belopp (SEK)</Form.Label><Form.Control type="number" min="0.01" step="0.01" value={form.amount} onChange={set('amount')} /></Form.Group>
        <Form.Group><Form.Label style={label}>Betalas senast</Form.Label><Form.Control type="date" value={form.requested_date} onChange={set('requested_date')} /></Form.Group>
        <Form.Group><Form.Label style={label}>Referens</Form.Label><Select options={REF_OPTIONS} value={refType} onChange={setRefType} theme={brandSelectTheme} isSearchable={false} /></Form.Group>
        {refType.value !== 'NONE' && (
          <Form.Group><Form.Label style={label}>{refType.value === 'SCOR' ? 'OCR-nummer' : 'Meddelande till mottagaren'}</Form.Label><Form.Control value={form.remittance_ref} onChange={set('remittance_ref')} maxLength={140} /></Form.Group>
        )}
      </div>
      <Form.Group style={{ marginTop: 10 }}>
        <Form.Label style={label}>Bokförs på konto</Form.Label>
        <AccountSelect options={options} allOptions={accounts} value={account} onChange={setAccount} placeholder="Välj motkonto (t.ex. hyra, leverantörsskuld)…" />
        <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>Betalningen bokförs mot det här kontot när bankhändelsen stäms av.</div>
      </Form.Group>
      {error && <div className="text-danger" style={{ fontSize: 13, marginTop: 8 }}>{error}</div>}
    </SettingsModal>
  );
};

export default ManualPaymentDialog;
